// Подсказки к ручкам: что делает, какие значения разумны, что слышно при промахе.
//
// Отдельным модулем, а не разметкой, по двум причинам. Тексты правятся целиком
// и часто, а в `index.html` пять килобайт прозы утопили бы сами контролы. И
// главное — модуль не трогает DOM на верхнем уровне, поэтому его можно
// импортировать в `node --test`; `settings.ts` из-за `getElementById` в теле
// модуля уже нельзя, и полноту подсказок иначе не проверить.
//
// Третье поле подсказки — не украшение. Почти всё уровневое здесь при промахе
// даёт тишину без единого сообщения об ошибке, и «что будет слышно» —
// единственный признак, по которому человек поймёт, что промахнулся именно
// тут, а не «сломалось».

/** Подсказка к одной ручке. */
export interface Help {
  /** Что ручка делает. */
  what: string;
  /** Какие значения разумны. */
  values?: string;
  /** Что будет слышно при промахе. */
  miss?: string;
}

/**
 * Ключи те же, что у `settings.knobs`.
 *
 * Совпадение стережёт `test/help.test.ts` в обе стороны: и забытая ручка, и
 * осиротевший текст — одинаковая ошибка.
 */
export const HELP: Record<string, Help> = {
  voice: {
    what: "Модель голоса, в который преобразуется речь.",
    values:
      "Список приезжает с сервера. У части голосов есть индекс — тогда " +
      "оживает ручка «Индекс». Смена голоса означает переподключение, поэтому " +
      "во время работы список заперт.",
  },
  inputDevice: {
    what: "Откуда берётся звук.",
    values:
      "Читается один раз, при подключении, поэтому во время работы список " +
      "заперт. Имена появляются только после разрешения на микрофон. Наушники " +
      "Bluetooth, выбранные микрофоном, уводят всю гарнитуру в узкополосный " +
      "профиль — берите микрофон отдельно от наушников.",
    miss:
      "На узкополосном моно модель звучит заметно глуше, и в счётчиках этого " +
      "не видно. Настоящая частота микрофона — в «Подробностях тракта».",
  },
  outputDevice: {
    what: "Куда играет преобразованный голос.",
    values:
      "Переключается на лету, в отличие от микрофона. Путь вывода показан в " +
      "«Подробностях тракта»: Chromium играет напрямую, Firefox — через " +
      "элемент звука со своей буферизацией.",
    miss:
      "При жалобе на задержку смотреть надо сюда в первую очередь. " +
      "Недоступное устройство звук не глушит: он уходит в системный выход.",
  },
  pitch: {
    what: "Сдвиг высоты голоса в полутонах — не в центах. Тембр не меняет.",
    values:
      "12 полутонов — октава: мужской голос в женский обычно +12, обратно " +
      "−12. Меняется на лету, переподключаться не нужно.",
    miss: "Слишком большой сдвиг звучит неестественно, но тишины не даёт.",
  },
  indexRatio: {
    what:
      "Доля признаков, подмешиваемых из индекса голоса: тембр становится " +
      "ближе к тому, на ком модель обучали.",
    values:
      "0 — выключен, дальше разумны 0.3–0.75. Меняется на лету. Индекс есть " +
      "не у каждого голоса — тогда ручка заблокирована.",
    miss: "Выше — ближе к донору, но глуше и с кашей на согласных.",
  },
  inputGain: {
    what:
      "Усиление входа самым первым узлом, до всех порогов. Всё, что дальше " +
      "судит по громкости, видит уже приведённый уровень.",
    values:
      "Браузер отдаёт микрофон заметно тише нативного захвата. Нажмите " +
      "«Проверить микрофон» — усиление посчитается по замеру, с оглядкой на " +
      "пик. Индикатор входа меряет до усиления: при +12 дБ сервер слышит " +
      "вчетверо громче показанного. Черты порогов на нём это уже учитывают.",
    miss:
      "Мало — четыре порога ниже по цепочке промахиваются разом, и на выходе " +
      "тишина. Много — клиппинг, а он необратим.",
  },
  highpass: {
    what:
      "Срезает всё ниже указанной частоты. Единственный безопасный шумодав " +
      "здесь: режет полосу, а не уровень, поэтому тихую речь не трогает.",
    values:
      "80 Гц — отсюда и начинать против шума. Убирает ветер и гул, которые " +
      "модель превращает в мычание.",
    miss:
      "Заметно выше 120 Гц — из мужского голоса уходит основной тон, звук " +
      "становится тонким и бедным.",
  },
  rnnoise: {
    what:
      "Шумодав, работающий спектрально: он судит не по громкости и потому не " +
      "срезает тихие слоги целиком, как это делает гейт.",
    values:
      "Стоит 10 мс задержки, замерено. Требует полной частоты входа, поэтому " +
      "вместе с ним экономия канала недоступна.",
    miss:
      "На тихом входе вычищает речь вместе с шумом. Если «глотает слова» — " +
      "выключать первым.",
  },
  gate: {
    what: "Всё тише порога до модели не доезжает вовсе.",
    values:
      "0 дБ означает «выключен». Подбирать снизу вверх, начиная примерно с " +
      "−55 дБ, и на слух. Порог стоит красной чертой на индикаторе входа: " +
      "речь должна заходить правее неё.",
    miss:
      "Узел уровневый: на тихом слоге он ошибается ровно так же, как на шуме. " +
      "Слишком высокий порог съедает начала фраз и тихие окончания.",
  },
  vad: {
    what:
      "Детектор речи: на тишине инференс не гоняется вовсе — экономит " +
      "видеокарту и убирает бормотание в паузах.",
    values:
      "Silero судит моделью речи, энергетический — по громкости. Это разные " +
      "приборы, а не «точнее и грубее». Silero есть не на каждом сервере; " +
      "если модели нет, детектор выключается, а не подменяется вторым.",
    miss:
      "Энергетический на тихом микрофоне глушит речь целиком, и выглядит это " +
      "как поломка сервера.",
  },
  vadThreshold: {
    what: "Насколько уверенно должно звучать, чтобы блок считался речью.",
    values:
      "Шкала зависит от режима: у Silero это вероятность речи (умолчание " +
      "0.5), у энергетического — среднеквадратичный уровень (0.02). " +
      "Переносить число между режимами нельзя, при смене режима порог " +
      "сбрасывается сам. По уровню — порог стоит чертой на индикаторе " +
      "входа, речь должна заходить правее; у Silero черты нет: снижайте от " +
      "0.5 вниз, если речь не проходит.",
    miss:
      "Порог из чужой шкалы — детектор не открывается почти никогда: полная " +
      "тишина на выходе и ни одного сообщения об ошибке.",
  },
  vadRelease: {
    what: "Сколько держать проход открытым после последней распознанной речи.",
    values: "200 мс по умолчанию; длиннее — целее хвосты слов.",
    miss:
      "Развилка неустранимая: пока держится удержание, вместе с хвостом слова " +
      "проходит и стук, попавший в эту секунду.",
  },
  protect: {
    what:
      "Не подмешивать индекс на беззвучных кадрах — иначе портятся согласные.",
    values:
      "0.5 означает «защита выключена», так принято в исходном RVC; меньше — " +
      "сильнее. Меняется на лету. Смысл имеет только при включённом индексе.",
    miss: "Без защиты шипящие и взрывные звучат смазанно.",
  },
  f0Threshold: {
    what:
      "Ниже какой уверенности кадр считается беззвучным и лишается высоты тона.",
    values: "0.03 по умолчанию. Выше 0.1 поднимать осторожно.",
    miss:
      "Глушит тихие озвученные кадры — слышно это как проглоченные слова, а " +
      "не как поломку.",
  },
  jitter: {
    what:
      "Запас принятого звука перед воспроизведением. Прямо прибавляется к " +
      "задержке.",
    values:
      "Умолчание 100 мс. Если растёт счётчик «срывы» и слышны щелчки — " +
      "поднимать до 150–200.",
    miss:
      "Мало — заикание на каждой неровности сети. Много — лишняя задержка " +
      "ровно на эту величину.",
  },
  blockMs: {
    what:
      "Каким куском клиент режет поток и отправляет на сервер. Это не то же, " +
      "что «Контекст»: контекст — прошлое, блок — свежее.",
    values:
      "Меньше блок — меньше задержка и выше нагрузка: сумма стадий инференса " +
      "обязана уложиться в блок.",
    miss:
      "Слишком мелкий блок сервер не успевает считать, очередь переполняется " +
      "и блоки отбрасываются — слышно как проглоченные слоги.",
  },
  contextMs: {
    what: "Сколько прошлого звука видит инференс сверх текущего блока.",
    values: "500 мс. Больше — ровнее и дороже.",
    miss: "Короче примерно 300 мс слышно как дрожание тона.",
  },
  crossfadeMs: {
    what: "Длина плавного перехода на стыке соседних блоков.",
    values: "50 мс.",
    miss:
      "Слишком короткий — щелчки на стыках; слишком длинный — смазанные " +
      "атаки согласных.",
  },
  solaMs: {
    what:
      "Окно, в котором ищется место сшивки соседних блоков по совпадению фазы.",
    values: "12 мс.",
    miss:
      "Ноль — фаза на стыке не совпадает, и появляется звенящий «электронный» " +
      "призвук. Больше нужного — просто лишняя работа.",
  },
  thrifty: {
    what: "Отправлять вверх 16 кГц вместо 48.",
    values:
      "Сервер всё равно приводит вход к своим 16 кГц; полная частота нужна " +
      "ровно одному узлу — шумодаву. При нём экономия и недоступна.",
    miss:
      "Без экономии поток втрое больше, и на неровной сети каждый лишний байт " +
      "превращается в выброшенный блок, то есть в проглоченный слог.",
  },
};

/**
 * Дорисовать кнопку «?» и пузырь у ручки.
 *
 * Не разметкой, а кодом: двадцать подсказок в `index.html` утопили бы контролы,
 * а ссылка на текст из одного места дешевле в правке. Побочное свойство даже
 * полезно — при сломанном или застрявшем в кэше бандле кнопок просто не будет,
 * а страница останется рабочей, как и всё остальное здесь.
 */
export function attachHelp(id: string, control: Element): void {
  const entry = HELP[id];
  if (entry === undefined) {
    // Молча пропущенная ручка выглядит как «так и задумано», а это ровно то,
    // чего в этом проекте не делают.
    console.warn(`подсказка для ручки «${id}» не написана`);
    return;
  }

  // `:scope > label`, а не просто `label`: у чекбоксов подпись оборачивает сам
  // контрол, и без ограничения на прямого потомка легко зацепить не ту.
  const label = control.closest(".row")?.querySelector(":scope > label");
  if (!label) {
    console.warn(`не нашлась подпись у ручки «${id}»`);
    return;
  }

  const bubbleId = `help-${id}`;
  const whatId = `${bubbleId}-what`;

  const bubble = document.createElement("span");
  bubble.className = "help-bubble";
  bubble.id = bubbleId;
  bubble.setAttribute("role", "tooltip");
  for (const text of [entry.what, entry.values, entry.miss]) {
    if (text === undefined) {
      continue;
    }
    const line = document.createElement("span");
    line.className = "help-line";
    line.textContent = text;
    if (bubble.childElementCount === 0) {
      line.id = whatId;
    }
    bubble.append(line);
  }

  const button = document.createElement("button");
  button.type = "button";
  button.className = "help-icon";
  button.textContent = "?";
  // Двадцать кнопок с одинаковым «Что это» диктор читает неразличимо, поэтому
  // имя берётся у подписи. У чекбоксов подпись — вся строка целиком, и это
  // честнее обрезки: длинное имя читается, обрезанное вводит в заблуждение.
  button.setAttribute("aria-label", `Подсказка: ${label.textContent?.trim() ?? id}`);

  const holder = document.createElement("span");
  holder.className = "help";
  holder.append(button, bubble);
  label.after(holder);

  // Контролу — только первая часть, кнопке — вся подсказка.
  //
  // Описание на контроле диктор читает перед каждым переходом на ползунок, и
  // все три части там означали бы полминуты речи до числа, ради которого туда
  // и пришли. «Что делает» — то, что нужно всегда; значения и симптом промаха
  // остаются за кнопкой, которая ровно для этого и стоит рядом.
  control.setAttribute("aria-describedby", whatId);
  button.setAttribute("aria-describedby", bubbleId);
}

/**
 * Прикалывание подсказки и её снятие: клик, уход фокуса, Escape.
 *
 * Три делегированных слушателя, а не по три на каждую из двадцати кнопок.
 * Наведение и клавиатуру ведёт CSS; коду остаётся ровно один случай — палец,
 * у которого наведения нет вовсе, и подсказка обязана оставаться на экране
 * после того, как палец убрали.
 *
 * Пометка при этом не различает, чем по кнопке щёлкнули: приколотая мышью
 * подсказка так же остаётся на экране после того, как указатель ушёл, — до
 * следующего клика, Escape или ухода фокуса. Пока указатель на самой кнопке,
 * снятие пометки не видно: пузырь держится наведением, и повторный клик
 * выглядит как «не нажалось». Это цена одного правила на оба ввода, и она
 * меньше, чем у двух разных.
 */
export function installHelpDismiss(): void {
  document.addEventListener("click", (event) => {
    const target = event.target instanceof Element ? event.target : null;
    const button = target?.closest<HTMLElement>(".help-icon") ?? null;
    const wasPinned = button?.dataset["pinned"] !== undefined;

    unpinAll();

    // Повторный клик по той же кнопке снимает, клик по соседней —
    // переключает: обе ветки получаются из одного `wasPinned`.
    if (button !== null && !wasPinned) {
      button.dataset["pinned"] = "";
    }
  });

  // Ушли табом или кликнули куда угодно ещё — пометка снимается вместе с
  // фокусом. Без этого приколотая подсказка висит поверх строк сколько угодно
  // долго, и наткнуться на неё можно спустя минуты после того, как о ней
  // забыли.
  document.addEventListener("focusout", (event) => {
    if (event.target instanceof Element && event.target.matches(".help-icon")) {
      unpinAll();
    }
  });

  document.addEventListener("keydown", (event) => {
    if (event.key === "Escape") {
      unpinAll();
    }
  });
}

function unpinAll(): void {
  for (const button of document.querySelectorAll<HTMLElement>(".help-icon[data-pinned]")) {
    delete button.dataset["pinned"];
  }
}
